// Llave de idempotencia por intento del usuario: el reintento del mismo formulario manda la misma
// llave (el silo devuelve el mismo resultado) y un formulario distinto manda otra.
import { ApiError } from '@/api/errors';

export interface IdempotentAttempt {
    /** La llave del intento con este cuerpo; la misma mientras el intento no se cierre. */
    keyFor(payload: unknown): string;
    /** Manda la escritura con su llave; un doble clic con el mismo cuerpo reusa la promesa en vuelo. */
    run<T>(payload: unknown, send: (key: string) => Promise<T>): Promise<T>;
    /** Cierra los intentos que no están en vuelo: el mismo formulario pasa a ser otra operación. */
    settle(): void;
    /** Descarta la llave de este cuerpo (el silo la rechazó por reusada con otros datos). */
    renew(payload: unknown): void;
}

/** JSON con las propiedades ordenadas, para que el orden de captura no cambie la llave. */
function fingerprintOf(value: unknown): string {
    const json = JSON.stringify(value, (_key, nested: unknown) => {
        if (nested === null || typeof nested !== 'object' || Array.isArray(nested)) return nested;
        return Object.fromEntries(
            Object.entries(nested as Record<string, unknown>).sort(([a], [b]) =>
                a.localeCompare(b),
            ),
        );
    }) as string | undefined;
    return json ?? '';
}

export function createIdempotentAttempt(
    generateKey: () => string = () => crypto.randomUUID(),
): IdempotentAttempt {
    const keys = new Map<string, string>();
    const inFlight = new Map<string, Promise<unknown>>();
    const keyOf = (fingerprint: string) => {
        const existing = keys.get(fingerprint);
        if (existing) return existing;
        const key = generateKey();
        keys.set(fingerprint, key);
        return key;
    };
    return {
        keyFor: (payload) => keyOf(fingerprintOf(payload)),
        run<T>(payload: unknown, send: (key: string) => Promise<T>): Promise<T> {
            const fingerprint = fingerprintOf(payload);
            const flying = inFlight.get(fingerprint);
            if (flying) return flying as Promise<T>;
            const promise = send(keyOf(fingerprint)).then(
                (result) => {
                    inFlight.delete(fingerprint);
                    keys.delete(fingerprint);
                    return result;
                },
                (error: unknown) => {
                    inFlight.delete(fingerprint);
                    throw error;
                },
            );
            inFlight.set(fingerprint, promise);
            return promise;
        },
        settle() {
            for (const fingerprint of [...keys.keys()]) {
                if (!inFlight.has(fingerprint)) keys.delete(fingerprint);
            }
        },
        renew(payload) {
            const fingerprint = fingerprintOf(payload);
            if (!inFlight.has(fingerprint)) keys.delete(fingerprint);
        },
    };
}

// Los intentos viven fuera del componente: cerrar y reabrir un diálogo no pierde la llave.
const registry = new Map<string, IdempotentAttempt>();

/** El intento compartido de una operación sobre un recurso (p. ej. `price:A15`). */
export function attemptForScope(scope: string): IdempotentAttempt {
    let attempt = registry.get(scope);
    if (!attempt) {
        attempt = createIdempotentAttempt();
        registry.set(scope, attempt);
    }
    return attempt;
}

/** Olvida el intento de un recurso: su siguiente envío lleva otra llave. */
export function forgetAttempt(scope: string): void {
    registry.delete(scope);
}

/** Solo para pruebas. */
export function resetAttemptRegistry(): void {
    registry.clear();
}

/** Mensajes de `fetch` sin respuesta en Chrome, Firefox y Safari. */
const NETWORK_FAILURE = /failed to fetch|networkerror|load failed|network request failed/i;

/** Un 5xx, la red caída o el timeout se reintentan con la MISMA llave; un 4xx no. */
export function isRetryableWriteError(error: unknown): boolean {
    if (error instanceof ApiError) return error.httpStatus >= 500;
    if (error instanceof TypeError) return NETWORK_FAILURE.test(error.message);
    return error instanceof Error && error.name === 'RequestTimeoutError';
}
